/**
 * Custom hook for fetching and managing widget settings
 */

import { useState, useEffect } from 'react';
import type { WidgetSettingsResponse } from '@/types';
import { fetchWidgetSettings } from '@/services/widgetApi';

interface UseWidgetSettingsResult {
  data: WidgetSettingsResponse['data'] | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch widget settings for a practice
 */
export function useWidgetSettings(practiceSlug: string): UseWidgetSettingsResult {
  const [data, setData] = useState<WidgetSettingsResponse['data'] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    if (!practiceSlug) {
      setError('Practice slug is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Note: Service worker unregistration is handled in App.tsx
      // Just make the API call here
      console.log('🌐 [useWidgetSettings] Making API call for practice slug:', practiceSlug);
      const response = await fetchWidgetSettings(practiceSlug);

      console.log('🌐 [useWidgetSettings] API response received:', {
        statusCode: response.status.code,
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
      });

      if (response.status.code === 200) {
        console.log('✅ [useWidgetSettings] Setting widget data:', response.data);
        setData(response.data);
      } else {
        setError(response.status.message || 'Failed to fetch widget settings');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      console.error('Error in useWidgetSettings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch data - fetchData will handle service worker unregistration
    fetchData();
  }, [practiceSlug]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
}
